import { useState, useCallback, useRef } from 'react';
import {
  getMarketplaceModules,
  getModuleDetail,
  installModule,
  uninstallModule,
  toggleModule,
  submitReview,
  type MarketplaceModuleResponse,
  type MarketplaceReview,
} from '../services/marketplaceService';

export function useMarketplace() {
  const [modules, setModules] = useState<MarketplaceModuleResponse[]>([]);
  const [selectedModule, setSelectedModule] = useState<MarketplaceModuleResponse | null>(null);
  const [reviews, setReviews] = useState<MarketplaceReview[]>([]);
  const [isLoadingModules, setIsLoadingModules] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentFilters = useRef<{ category?: string; isPremade?: boolean; search?: string }>({});

  // ── Fetch module list ──────────────────────────────────────────────────────
  const fetchModules = useCallback(
    async (category?: string, isPremade?: boolean, search?: string) => {
      currentFilters.current = { category, isPremade, search };
      setIsLoadingModules(true);
      setError(null);
      try {
        const data = await getMarketplaceModules(
          category === 'All' ? undefined : category,
          isPremade,
          search,
        );
        setModules(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load modules');
      } finally {
        setIsLoadingModules(false);
      }
    },
    [],
  );

  // Silent refetch with stored filters (called after install/uninstall/toggle)
  const refetchModules = useCallback(async () => {
    const { category, isPremade, search } = currentFilters.current;
    try {
      const data = await getMarketplaceModules(
        category === 'All' ? undefined : category,
        isPremade,
        search,
      );
      setModules(data);
    } catch (err) {
      console.error('refetch failed:', err);
    }
  }, []);

  // ── Module detail ──────────────────────────────────────────────────────────
  const selectModule = useCallback(async (moduleId: string) => {
    if (!moduleId) return;
    setIsLoadingDetail(true);
    setError(null);
    try {
      const detail = await getModuleDetail(moduleId);
      setSelectedModule(detail.module);
      setReviews(detail.reviews);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load module detail');
    } finally {
      setIsLoadingDetail(false);
    }
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedModule(null);
    setReviews([]);
  }, []);

  // ── Install ────────────────────────────────────────────────────────────────
  const handleInstall = useCallback(
    async (moduleId: string) => {
      setIsProcessing(true);
      setError(null);
      try {
        await installModule(moduleId, true);
        // Get fresh state from server — no optimistic updates
        await refetchModules();
        if (selectedModule?.moduleId === moduleId || !moduleId) {
          // Update detail view too if we're on this module's detail
          const detail = await getModuleDetail(moduleId);
          setSelectedModule(detail.module);
          setReviews(detail.reviews);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Install failed');
      } finally {
        setIsProcessing(false);
      }
    },
    [refetchModules, selectedModule],
  );

  // ── Uninstall ──────────────────────────────────────────────────────────────
  const handleUninstall = useCallback(
    async (moduleId: string) => {
      setIsProcessing(true);
      setError(null);
      try {
        await uninstallModule(moduleId);
        await refetchModules();
        if (selectedModule?.moduleId === moduleId || !moduleId) {
          const detail = await getModuleDetail(moduleId);
          setSelectedModule(detail.module);
          setReviews(detail.reviews);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Uninstall failed');
      } finally {
        setIsProcessing(false);
      }
    },
    [refetchModules, selectedModule],
  );

  const handleToggle = useCallback(
    async (moduleId: string, isEnabled: boolean) => {
      setIsProcessing(true);
      setError(null);
      try {
        await toggleModule(moduleId, isEnabled);
        await refetchModules();
        if (selectedModule?.moduleId === moduleId || !moduleId) {
          const detail = await getModuleDetail(moduleId);
          setSelectedModule(detail.module);
          setReviews(detail.reviews);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Toggle failed');
      } finally {
        setIsProcessing(false);
      }
    },
    [refetchModules, selectedModule],
  );

  const handleSubmitReview = useCallback(
    async (moduleId: string, reviewerName: string, rating: number, comment: string) => {
      setIsProcessing(true);
      setError(null);
      try {
        await submitReview(moduleId, reviewerName, rating, comment);
        const detail = await getModuleDetail(moduleId);
        setSelectedModule(detail.module);
        setReviews(detail.reviews);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Review failed');
      } finally {
        setIsProcessing(false);
      }
    },
    [],
  );

  return {
    modules,
    selectedModule,
    reviews,
    isLoadingModules,
    isLoadingDetail,
    isProcessing,
    error,
    fetchModules,
    selectModule,
    clearSelection,
    handleInstall,
    handleUninstall,
    handleToggle,
    handleSubmitReview,
  };
}
